import { useCallback, useEffect } from "react";
import { FormProvider, get, useForm } from "react-hook-form";

import type { z } from "zod";
import { produce } from "immer";
import debounce from "lodash.debounce";

import { t } from "@lingui/macro";
import { zodResolver } from "@hookform/resolvers/zod";
import { useResumeStore } from "@/client/stores/resume";
import { T } from "@lingui/react/dist/shared/react.e5f95de8";
import { useDialog, DialogName } from "@/client/stores/dialog";
import { useSaveStatusStore } from "@/client/stores/useSaveStatusStore";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Slider,
} from "@reactive-resume/ui";
import { defaultLanguage, languageSchema, SectionWithItem } from "@reactive-resume/schema";

import { useAutosaveStatus } from "../../../_components/SavingIndicator";

const formSchema = languageSchema;

type FormValues = z.infer<typeof formSchema>;

export const LanguagesForm = ({ id }: { id: string }) => {
  const { payload } = useDialog<T>(id as DialogName);

  const form = useForm<FormValues>({
    defaultValues: { ...defaultLanguage, ...payload?.item },
    resolver: zodResolver(formSchema),
  });

  const setValue = useResumeStore((state) => state.setValue);
  const section = useResumeStore((state) => {
    return get(state.resume.data.sections, id);
  }) as SectionWithItem<T> | null;

  const onSubmit = (values: T) => {
    if (!section) return;

    if (!payload.item?.id) return;

    setValue(
      `sections.${id}.items`,
      produce(section.items, (draft: T[]): void => {
        const index = draft.findIndex((item) => item.id === payload.item?.id);
        if (index === -1) return;
        draft[index] = values;
      }),
    );
  };

  const { status, triggerSave } = useAutosaveStatus({
    saveFn: async () => {
      const values = form.getValues();
      onSubmit(values);
    },
    debounceMs: 500,
    minSavingMs: 300,
    savedVisibleMs: 1200,
  });

  const setStatus = useSaveStatusStore((s) => s.setStatus);

  useEffect(() => {
    setStatus(status);
  }, [status, setStatus]);

  const debouncedSubmit = useCallback(
    debounce(() => {
      triggerSave(); // notify status hook
    }, 500),
    [form, triggerSave],
  );

  return (
    <FormProvider {...form}>
      <form className="max-h-[60vh] space-y-6 lg:max-h-fit" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 gap-4 p-1 sm:grid-cols-2">
          <FormField
            name="name"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t`Name`}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-white"
                    onChange={(e) => {
                      field.onChange(e);
                      debouncedSubmit();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="description"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t`Description`}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-white"
                    onChange={(e) => {
                      field.onChange(e);
                      debouncedSubmit();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="level"
            control={form.control}
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>{t`Level`}</FormLabel>
                <FormControl className="py-2">
                  <div className="flex items-center gap-x-4">
                    <Slider
                      {...field}
                      min={0}
                      max={5}
                      value={[field.value]}
                      onValueChange={(value) => {
                        field.onChange(value[0]);
                        debouncedSubmit();
                      }}
                    />

                    {field.value > 0 ? (
                      <span className="text-base font-bold">{field.value}</span>
                    ) : (
                      <span className="text-base font-bold">{t`Hidden`}</span>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </FormProvider>
  );
};
